import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { financeiroApi } from '../../../services/api';
import { ContaReceber } from '../../../types';
import { format, differenceInDays, parseISO } from 'date-fns';
import {
  PlusIcon,
  MagnifyingGlassIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  ClockIcon,
  XCircleIcon,
  DocumentTextIcon
} from '@heroicons/react/24/outline';

export const ContasReceberList = () => {
  const [contas, setContas] = useState<ContaReceber[]>([]);
  const [loading, setLoading] = useState(true);
  const [filtroStatus, setFiltroStatus] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const navigate = useNavigate();

  const [vencimentoMin, setVencimentoMin] = useState('');
  const [vencimentoMax, setVencimentoMax] = useState('');
  const [quickFilter, setQuickFilter] = useState('');
  const [valorMin, setValorMin] = useState('');
  const [valorMax, setValorMax] = useState('');

  useEffect(() => {
    const timer = setTimeout(() => {
      if (searchInput.length === 0 || searchInput.length >= 3) {
        setSearchTerm(searchInput);
      }
    }, 500);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const loadContas = async () => {
    try {
      setLoading(true);
      const params: any = { page_size: 100 };
      if (filtroStatus) params.status = filtroStatus;
      if (searchTerm) params.search = searchTerm;
      if (vencimentoMin) params.vencimento_min = vencimentoMin;
      if (vencimentoMax) params.vencimento_max = vencimentoMax;
      if (quickFilter) params[quickFilter] = 'true';
      if (valorMin) params.valor_min = valorMin;
      if (valorMax) params.valor_max = valorMax;

      const response = await financeiroApi.getContasReceber(params);
      setContas(response.results || []);
    } catch (error) {
      console.error('Erro ao carregar contas a receber:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContas();
  }, [filtroStatus, searchTerm, vencimentoMin, vencimentoMax, quickFilter, valorMin, valorMax]);

  const handleDelete = async (id: number) => {
    if (!window.confirm('Excluir esta conta a receber?')) return;
    try {
      await financeiroApi.deleteContaReceber(id);
      loadContas();
    } catch (error) {
      console.error('Erro ao excluir:', error);
      alert('Erro ao excluir');
    }
  };

  const handleConfirmarRecebimento = async (id: number) => {
    const hoje = format(new Date(), 'dd/MM/yyyy');
    const dataDigitada = prompt(`Data do recebimento (DD/MM/AAAA)\nDeixe em branco para hoje (${hoje}):`) || hoje;
    if (!dataDigitada) return;

    const dateRegexBR = /^(\d{2})\/(\d{2})\/(\d{4})$/;
    const match = dataDigitada.match(dateRegexBR);
    if (!match) {
      alert('Data inválida! Use DD/MM/AAAA');
      return;
    }

    const [, dia, mes, ano] = match;
    const dataRecebimentoISO = `${ano}-${mes}-${dia}`;

    try {
      await financeiroApi.confirmarRecebimentoContaReceber(id, { data_pagamento: dataRecebimentoISO });
      loadContas();
    } catch (error: any) {
      const msg = error.response?.data?.detail || error.response?.data?.error || 'Erro ao confirmar';
      alert(msg);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);
  };

  const calculateDiasAtraso = (dataVencimento: string, status: string) => {
    if (status === 'C' || status === 'CA') return 0;
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const vencimento = parseISO(dataVencimento);
    const dias = differenceInDays(today, vencimento);
    return dias > 0 ? dias : 0;
  };

  const getStatusBadgeClass = (status: string) => {
    switch (status) {
      case 'P': return 'bg-yellow-100 text-yellow-800';
      case 'C': return 'bg-green-100 text-green-800';
      case 'CA': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'P': return <ClockIcon className="w-3 h-3" />;
      case 'C': return <CheckCircleIcon className="w-3 h-3" />;
      case 'CA': return <XCircleIcon className="w-3 h-3" />;
      default: return null;
    }
  };

  return (
    <div className="space-y-2">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-base font-bold text-secondary-900">Contas a Receber</h1>
          <p className="text-xs text-secondary-600 mt-0.5">Gerencie suas contas e recebimentos</p>
        </div>
        <Link to="/financeiro/contas-receber/novo" className="px-3 py-1 text-xs font-medium text-white bg-primary-600 rounded hover:bg-primary-700 flex items-center gap-1">
          <PlusIcon className="h-3.5 w-3.5" />
          Nova
        </Link>
      </div>

      {/* Filtros */}
      <div className="bg-white border border-secondary-200 rounded p-2">
        <div className="flex flex-wrap items-end gap-2">
          <div className="flex-1 min-w-[180px]">
            <div className="relative">
              <MagnifyingGlassIcon className="absolute left-2 top-1/2 transform -translate-y-1/2 h-3.5 w-3.5 text-secondary-400" />
              <input
                type="text"
                placeholder="Buscar (mín. 3 caracteres)..."
                className="w-full pl-7 pr-2 py-1 text-xs border border-secondary-300 rounded"
                value={searchInput}
                onChange={(e) => setSearchInput(e.target.value)}
              />
            </div>
          </div>

          <select
            className="px-2 py-1 text-xs border border-secondary-300 rounded"
            value={filtroStatus}
            onChange={(e) => setFiltroStatus(e.target.value)}
          >
            <option value="">Todos status</option>
            <option value="P">Pendente</option>
            <option value="C">Confirmado</option>
            <option value="CA">Cancelado</option>
          </select>

          <div>
            <label className="block text-xs text-secondary-600 mb-0.5">De</label>
            <input
              type="date"
              value={vencimentoMin}
              onChange={(e) => setVencimentoMin(e.target.value)}
              className="px-2 py-1 text-xs border border-secondary-300 rounded w-28"
            />
          </div>

          <div>
            <label className="block text-xs text-secondary-600 mb-0.5">Até</label>
            <input
              type="date"
              value={vencimentoMax}
              onChange={(e) => setVencimentoMax(e.target.value)}
              className="px-2 py-1 text-xs border border-secondary-300 rounded w-28"
            />
          </div>

          <div className="flex flex-wrap gap-1 ml-auto">
            {['vencidas', 'vence_hoje', 'vence_7_dias', 'vence_30_dias'].map((filter) => {
              const labels: Record<string, string> = {
                'vencidas': 'Vencidas',
                'vence_hoje': 'Hoje',
                'vence_7_dias': '7 dias',
                'vence_30_dias': '30 dias'
              };
              const colors: Record<string, string> = {
                'vencidas': 'bg-red-100 text-red-700 border-red-300',
                'vence_hoje': 'bg-orange-100 text-orange-700 border-orange-300',
                'vence_7_dias': 'bg-yellow-100 text-yellow-700 border-yellow-300',
                'vence_30_dias': 'bg-blue-100 text-blue-700 border-blue-300'
              };
              return (
                <button
                  key={filter}
                  type="button"
                  onClick={() => setQuickFilter(quickFilter === filter ? '' : filter)}
                  className={`px-2 py-1 text-xs rounded ${
                    quickFilter === filter
                      ? colors[filter] + ' border'
                      : 'bg-secondary-100 text-secondary-700 hover:bg-secondary-200'
                  }`}
                >
                  {labels[filter]}
                </button>
              );
            })}
          </div>
        </div>

        {(vencimentoMin || vencimentoMax || quickFilter || valorMin || valorMax) && (
          <div className="flex justify-end mt-1">
            <button
              type="button"
              onClick={() => { setVencimentoMin(''); setVencimentoMax(''); setQuickFilter(''); setValorMin(''); setValorMax(''); }}
              className="text-xs text-secondary-500 hover:text-secondary-700 underline"
            >
              Limpar filtros
            </button>
          </div>
        )}
      </div>

      {/* Tabela */}
      <div className="bg-white rounded border border-secondary-200 overflow-hidden">
        {loading ? (
          <div className="flex justify-center items-center h-24">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-primary-600"></div>
          </div>
        ) : contas.length === 0 ? (
          <div className="text-center py-3 text-xs text-secondary-500">
            <DocumentTextIcon className="h-8 w-8 text-secondary-300 mx-auto mb-1" />
            Nenhuma conta encontrada
          </div>
        ) : (
          <table className="w-full">
            <thead className="bg-secondary-50">
              <tr>
                <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500">Cliente</th>
                <th className="px-2 py-1 text-right text-xs font-medium text-secondary-500">Valor</th>
                <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500 hidden sm:table-cell">Vencimento</th>
                <th className="px-2 py-1 text-center text-xs font-medium text-secondary-500">Status</th>
                <th className="px-2 py-1 text-center text-xs font-medium text-secondary-500 hidden md:table-cell">Atraso</th>
                <th className="px-2 py-1 text-left text-xs font-medium text-secondary-500 hidden lg:table-cell">NF</th>
                <th className="px-2 py-1 text-right text-xs font-medium text-secondary-500">Ações</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-secondary-100">
              {contas.map((conta) => {
                const diasAtraso = calculateDiasAtraso(conta.data_vencimento, conta.status);
                return (
                  <tr key={conta.id} className="hover:bg-secondary-25">
                    <td className="px-2 py-1 text-xs text-secondary-900 truncate max-w-[150px] sm:max-w-[200px]" title={conta.client_nome || ''}>
                      {conta.client_nome || '-'}
                    </td>
                    <td className="px-2 py-1 text-xs text-right font-medium text-green-600 whitespace-nowrap">
                      {formatCurrency(Number(conta.valor))}
                    </td>
                    <td className="px-2 py-1 text-xs text-secondary-600 hidden sm:table-cell whitespace-nowrap">
                      {format(parseISO(conta.data_vencimento), 'dd/MM/yyyy')}
                    </td>
                    <td className="px-2 py-1 text-center">
                      <span className={`inline-flex items-center gap-0.5 px-1.5 py-0.5 rounded text-xs font-medium ${getStatusBadgeClass(conta.status)}`}>
                        {getStatusIcon(conta.status)}
                        <span className="hidden sm:inline">{conta.status_display}</span>
                      </span>
                    </td>
                    <td className="px-2 py-1 text-xs text-center hidden md:table-cell">
                      {diasAtraso > 0 ? (
                        <span className="text-red-600 font-medium">{diasAtraso}d</span>
                      ) : '-'}
                    </td>
                    <td className="px-2 py-1 text-xs text-secondary-500 hidden lg:table-cell">
                      {conta.numero_documento || '-'}
                    </td>
                    <td className="px-2 py-1 text-right">
                      <div className="flex justify-end gap-1">
                        <button
                          onClick={() => navigate(`/financeiro/contas-receber/${conta.id}`)}
                          className="p-1 text-primary-600 hover:text-primary-900"
                          title="Editar"
                        >
                          <PencilIcon className="h-3.5 w-3.5" />
                        </button>
                        {conta.status === 'P' && (
                          <button
                            onClick={() => handleConfirmarRecebimento(Number(conta.id))}
                            className="p-1 text-green-600 hover:text-green-900"
                            title="Confirmar"
                          >
                            <CheckCircleIcon className="h-3.5 w-3.5" />
                          </button>
                        )}
                        <button
                          onClick={() => handleDelete(Number(conta.id))}
                          className="p-1 text-red-600 hover:text-red-900"
                          title="Excluir"
                        >
                          <TrashIcon className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {/* Rodapé com total */}
      {contas.length > 0 && (
        <div className="text-xs text-secondary-500 text-right">
          {contas.length} registro(s) | Total listado: <span className="font-medium text-secondary-900">{formatCurrency(contas.reduce((sum, c) => sum + Number(c.valor), 0))}</span>
        </div>
      )}
    </div>
  );
};
